import React, { useEffect, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading } from './ui'

interface PdfModalProps {
  isOpen: boolean
  onClose: () => void
  pdfUrl: string | null
  title?: string
}

const PdfModal: React.FC<PdfModalProps> = ({ isOpen, onClose, pdfUrl, title = 'Visualizar PDF' }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Bloquear scroll do body quando modal estiver aberto
  useEffect(() => {
    if (!isOpen) return
    const body = document.body
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = previousOverflow
    }
  }, [isOpen])

  // Gerenciar tecla Escape
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen || !pdfUrl) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" 
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
            {title}
          </Heading>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Fechar modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden" style={{ height: 'calc(90vh - 88px)' }}>
          <iframe
            ref={iframeRef}
            src={pdfUrl}
            className="w-full h-full border-0"
            title={title}
          />
        </div>
      </div>
    </div>
  )
}

export default PdfModal
